import React, {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import useGooglePlaces, {
  getPlaceFullAddress,
  placeFromStored,
  placeToStored,
} from "../../hooks/useGooglePlaces";
import PlaceSuggestionLabel from "./PlaceSuggestionLabel";
import { useVehicles } from "../../hooks/VehicleContext";
import VehicleSelector from "./VehicleSelector";
import QuantityControl from "./QuantityControl";
import { getPickupMinLeadError } from "./DateSelectModal";
import {
  getBookingQuantityLimits,
  clampQuantities,
} from "../../utils/vehicleBookingLimits";
import axios from "axios";
import { toast } from "../../utils/toast";
import {
  validatePickupServiceArea,
  isOutsideServiceAreaMessage,
  hasResolvedPlaceCoords,
} from "../../utils/validatePickupServiceArea";
import {
  getStopRouteValidationError,
  validateStopsOnRoute,
} from "../../utils/validateStopOnRoute";
import {
  BOOKING_DRAFT_STORAGE_KEY,
  writeSessionBookingDraft,
} from "../../utils/bookingStorageKeys";
import {
  CHECK_FARE_TOAST_ID,
  flattenLaravelErrors,
  resolveApiPayloadMessage,
  resolveAxiosErrorMessage,
} from "../../utils/apiValidationErrors";

const RIDE_CITYTOUR = 2;

const toApiDateTimeParts = (value) => {
  if (!value) return { date: null, time: null };
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return { date: null, time: null };
  const date = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
  const time = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(
    2,
    "0",
  )}`;
  return { date, time };
};

export default function CityTours({
  onOpenDatePicker,
  onOpenHoursPicker,
  setBookingData,
  initialData,
  parentBookingSnapshot = null,
  checkoutRevisionRef = 0,
  onCheckFareConfigChange,
}) {
  const [hours, setHours] = useState("");
  const [pickup, setPickup] = useState({ address: "", lat: null, lng: null });
  const [stops, setStops] = useState([]);
  const [dropoff, setDropoff] = useState({ address: "", lat: null, lng: null });
  const [schedule, setSchedule] = useState("");
  const [activeField, setActiveField] = useState(null); // 'pickup', 'dropoff', or 'stop-index'
  const [owVehicle, setOwVehicle] = useState("");
  const [cityPassengers, setCityPassengers] = useState(1);
  const [citySuitcase, setCitySuitcase] = useState(0);
  const [fareLoading, setFareLoading] = useState(false);
  const [pickupServiceAreaError, setPickupServiceAreaError] = useState("");
  const [fareApiError, setFareApiError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const { vehicles } = useVehicles();
  const hoursInputRef = useRef(null);
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const scheduleInputRef = useRef(null);

  const citySelectedVehicle = useMemo(
    () => vehicles.find((v) => Number(v.id) === Number(owVehicle)),
    [vehicles, owVehicle],
  );
  const cityLimits = useMemo(
    () => getBookingQuantityLimits(citySelectedVehicle),
    [citySelectedVehicle],
  );

  const hydratedEditRef = useRef(null);

  useEffect(() => {
    const { passengers, suitcase } = clampQuantities({
      passengers: cityPassengers,
      suitcase: citySuitcase,
      maxPassengers: cityLimits.maxPassengers,
      maxSuitcase: cityLimits.maxSuitcase,
    });
    if (passengers !== cityPassengers) setCityPassengers(passengers);
    if (suitcase !== citySuitcase) setCitySuitcase(suitcase);
  }, [
    cityLimits.maxPassengers,
    cityLimits.maxSuitcase,
    cityPassengers,
    citySuitcase,
  ]);

  useEffect(() => {
    if (!initialData || initialData.ride_type !== RIDE_CITYTOUR) {
      hydratedEditRef.current = null;
      return;
    }
    if (hydratedEditRef.current === initialData) return;
    hydratedEditRef.current = initialData;

    setHours(initialData.city_hours || "");
    setPickup(
      placeFromStored({
        title: initialData.pickup_title,
        address: initialData.pickup || "",
        lat: initialData.pickup_lat || null,
        lng: initialData.pickup_lng || null,
      }),
    );
    setDropoff(
      placeFromStored({
        title: initialData.dropoff_title,
        address: initialData.dropoff || "",
        lat: initialData.dropoff_lat || null,
        lng: initialData.dropoff_lng || null,
      }),
    );
    setSchedule(initialData.schedule || "");
    if (initialData.stops && initialData.stops.length > 0) {
      setStops(
        initialData.stops.map((s) =>
          placeFromStored({
            title: s.title,
            address: s.address,
            lat: s.lat,
            lng: s.lng,
          }),
        ),
      );
    } else {
      setStops([]);
    }
    const vt = initialData.rides?.[0]?.vehicle_type ?? initialData.vehicle_type;
    if (vt) setOwVehicle(vt);
    setCityPassengers(initialData.no_of_passengers || 1);
    setCitySuitcase(initialData.suitcase || 1);
  }, [initialData]);

  const { suggestions, fetchSuggestions, getPlaceDetails, setSuggestions } =
    useGooglePlaces();

  const handlePlaceSelect = async (place, field) => {
    try {
      setFareApiError("");
      const details = await getPlaceDetails(place.place_id, place);
      if (field === "pickup") {
        setPickup(details);
        const v = await validatePickupServiceArea(details.lat, details.lng);
        setPickupServiceAreaError(v.ok ? "" : v.message || "");
      } else if (field === "dropoff") {
        setDropoff(details);
      } else if (field.startsWith("stop-")) {
        const index = parseInt(field.split("-")[1]);
        const routeErr = await getStopRouteValidationError(
          pickup,
          dropoff,
          details,
        );
        if (routeErr) {
          toast.error(routeErr);
          return;
        }
        const newStops = [...stops];
        newStops[index] = details;
        setStops(newStops);
      }
      setSuggestions([]);
      setActiveField(null);
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const addStop = () =>
    setStops([...stops, { address: "", lat: null, lng: null }]);
  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const isFormValid =
    hours &&
    pickup.address &&
    dropoff.address &&
    schedule &&
    hasResolvedPlaceCoords(pickup) &&
    hasResolvedPlaceCoords(dropoff) &&
    !pickupServiceAreaError;

  const focusInvalidField = useCallback((fieldKey) => {
    const refs = {
      hours: hoursInputRef,
      pickup: pickupInputRef,
      dropoff: dropoffInputRef,
      schedule: scheduleInputRef,
    };
    refs[fieldKey]?.current?.focus?.();
  }, []);

  const getFormValidationErrors = useCallback(
    (frm) => {
      if (!frm.hours) {
        return {
          errors: { hours: "Please select hours." },
          firstField: "hours",
        };
      }
      if (!frm.pickup.address) {
        return {
          errors: { pickup: "Pick-up is required." },
          firstField: "pickup",
        };
      }
      if (!hasResolvedPlaceCoords(frm.pickup)) {
        return {
          errors: {
            pickup: "Select pick-up from suggestions to confirm location.",
          },
          firstField: "pickup",
        };
      }
      if (!frm.dropoff.address) {
        return {
          errors: { dropoff: "Drop-off is required." },
          firstField: "dropoff",
        };
      }
      if (!hasResolvedPlaceCoords(frm.dropoff)) {
        return {
          errors: {
            dropoff: "Select drop-off from suggestions to confirm location.",
          },
          firstField: "dropoff",
        };
      }
      if (!frm.schedule) {
        return {
          errors: { schedule: "Pick-up time is required." },
          firstField: "schedule",
        };
      }
      const scheduleLeadErr = getPickupMinLeadError(frm.schedule);
      if (scheduleLeadErr) {
        return {
          errors: { schedule: scheduleLeadErr },
          firstField: "schedule",
        };
      }
      return { errors: {}, firstField: null };
    },
    [],
  );

  const buildCheckoutPreviewFromForm = useCallback(() => {
    const base = parentBookingSnapshot ?? initialData;
    if (!base || base.ride_type !== RIDE_CITYTOUR) return null;
    if (!isFormValid) return null;
    const scheduleParts = toApiDateTimeParts(schedule);
    const vt = owVehicle || base.rides?.[0]?.vehicle_type || "";
    const payload = {
      ride_type: RIDE_CITYTOUR,
      booking_type: 1,
      no_of_passengers: cityPassengers,
      suitcase: citySuitcase,
      rides: [
        {
          vehicle_type: vt,
          pickup_lat: pickup.lat,
          pickup_lng: pickup.lng,
          drop_lat: dropoff.lat,
          drop_lng: dropoff.lng,
          pickup_date: scheduleParts.date,
          pickup_time: scheduleParts.time,
          city_hours: hours,
          stops: stops
            .filter((s) => s.address && s.address.trim() !== "")
            .map((s) => ({ lat: s.lat, lng: s.lng })),
        },
      ],
    };
    return {
      ...base,
      ...payload,
      pickup: getPlaceFullAddress(pickup),
      pickup_title: pickup.address,
      pickup_lat: payload.rides[0].pickup_lat,
      pickup_lng: payload.rides[0].pickup_lng,
      dropoff: getPlaceFullAddress(dropoff),
      dropoff_title: dropoff.address,
      dropoff_lat: payload.rides[0].drop_lat,
      dropoff_lng: payload.rides[0].drop_lng,
      stops: stops
        .filter((s) => getPlaceFullAddress(s))
        .map((s) => placeToStored(s)),
      schedule,
      city_hours: hours,
      no_of_passengers: cityPassengers,
      suitcase: citySuitcase,
      fare_details: base.fare_details,
      rides: payload.rides,
    };
  }, [
    initialData,
    parentBookingSnapshot,
    isFormValid,
    schedule,
    pickup,
    dropoff,
    hours,
    owVehicle,
    stops,
    cityPassengers,
    citySuitcase,
  ]);

  useLayoutEffect(() => {
    if (!initialData || initialData.ride_type !== RIDE_CITYTOUR) return;
    const preview = buildCheckoutPreviewFromForm();
    if (!preview) return;
    try {
      const ts = Math.max(Date.now(), (checkoutRevisionRef || 0) + 1);
      sessionStorage.setItem(
        BOOKING_DRAFT_STORAGE_KEY,
        JSON.stringify({ ts, data: preview }),
      );
    } catch (_) {
      /* ignore */
    }
  }, [initialData, checkoutRevisionRef, buildCheckoutPreviewFromForm]);

  /** Latest form snapshot on each click (parent holds stable onClick — avoids stale pickup/drop coords). */
  const checkFareStateRef = useRef(null);
  checkFareStateRef.current = {
    isFormValid,
    schedule,
    pickup,
    dropoff,
    hours,
    owVehicle,
    stops,
    cityPassengers,
    citySuitcase,
  };

  const handleCheckFare = useCallback(async () => {
    const frm = checkFareStateRef.current;

    const stopErr = await validateStopsOnRoute(
      frm.pickup,
      frm.dropoff,
      frm.stops,
    );
    if (stopErr) {
      toast.error(stopErr.message);
      focusInvalidField("pickup");
      return;
    }

    const { errors, firstField } = getFormValidationErrors(frm);
    if (firstField) {
      setFormErrors(errors);
      focusInvalidField(firstField);
      return;
    }

    setFormErrors({});
    setFareLoading(true);
    setFareApiError("");
    toast.dismiss(CHECK_FARE_TOAST_ID);
    try {
      const scheduleParts = toApiDateTimeParts(frm.schedule);

      const payload = {
        ride_type: RIDE_CITYTOUR, // RIDE_CITYTOUR
        booking_type: 1, // ONE WAY
        no_of_passengers: frm.cityPassengers,
        suitcase: frm.citySuitcase,
        rides: [
          {
            vehicle_type: frm.owVehicle,
            pickup_lat: frm.pickup.lat,
            pickup_lng: frm.pickup.lng,
            drop_lat: frm.dropoff.lat,
            drop_lng: frm.dropoff.lng,
            pickup_date: scheduleParts.date,
            pickup_time: scheduleParts.time,
            city_hours: frm.hours,
            stops: frm.stops
              .filter((row) => row.address && row.address.trim() !== "")
              .map((row) => ({ lat: row.lat, lng: row.lng })),
          },
        ],
      };

      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/check-fare`,
        payload,
      );

      if (response.data.status) {
        setFareApiError("");
        toast.dismiss(CHECK_FARE_TOAST_ID);
        setPickupServiceAreaError("");
        const next = {
          ...payload,
          pickup: getPlaceFullAddress(frm.pickup),
          pickup_title: frm.pickup.address,
          pickup_lat: payload.rides[0].pickup_lat,
          pickup_lng: payload.rides[0].pickup_lng,
          dropoff: getPlaceFullAddress(frm.dropoff),
          dropoff_title: frm.dropoff.address,
          dropoff_lat: payload.rides[0].drop_lat,
          dropoff_lng: payload.rides[0].drop_lng,
          stops: frm.stops
            .filter((row) => getPlaceFullAddress(row))
            .map((row) => placeToStored(row)),
          schedule: frm.schedule,
          city_hours: frm.hours,
          no_of_passengers: frm.cityPassengers,
          suitcase: frm.citySuitcase,
          fare_details: response.data.fare_details,
          distance: response.data.fare_details?.distance_km ?? null,
          return_distance: response.data.fare_details?.return_distance_km ?? null,
        };
        setBookingData(next);
        writeSessionBookingDraft(next);

        setTimeout(() => {
          const modalElement = document.getElementById("updatePolicy-Modal");
          if (modalElement && window.bootstrap) {
            const modal =
              window.bootstrap.Modal.getOrCreateInstance(modalElement);
            modal.show();
          }
        }, 200);
      } else {
        const data = response.data;
        const inline = flattenLaravelErrors(data?.errors).join("\n");
        const msg =
          resolveApiPayloadMessage(data) ||
          inline.replace(/\n/g, " ").trim() ||
          "Something went wrong";
        setFareApiError(inline || msg);
        if (isOutsideServiceAreaMessage(msg)) {
          setPickupServiceAreaError(msg);
        }
        toast.error(msg, { toastId: CHECK_FARE_TOAST_ID });
      }
    } catch (error) {
      console.error("Fare check error:", error);
      const data = error.response?.data;
      const inline = flattenLaravelErrors(data?.errors).join("\n");
      const msg =
        resolveAxiosErrorMessage(error) ||
        inline.replace(/\n/g, " ").trim() ||
        "Failed to check fare. Please try again.";
      setFareApiError(inline || msg);
      if (isOutsideServiceAreaMessage(msg)) {
        setPickupServiceAreaError(msg);
      }
      toast.error(msg, { toastId: CHECK_FARE_TOAST_ID });
    } finally {
      setFareLoading(false);
    }
  }, [focusInvalidField, getFormValidationErrors, setBookingData]);

  useEffect(() => {
    if (!onCheckFareConfigChange) return;
    onCheckFareConfigChange({
      onClick: handleCheckFare,
      disabled: fareLoading,
      label: fareLoading ? "Calculating..." : "Check Fare",
    });
  }, [onCheckFareConfigChange, fareLoading, handleCheckFare]);

  return (
    <div
      className="tab-pane fade active show"
      id="nav-profile"
      role="tabpanel"
      aria-labelledby="nav-profile-tab"
    >
      <div className="second_tabs">
        <h2>City Tour</h2>
        {fareApiError ? (
          <div
            className="alert alert-danger my-2"
            role="alert"
            style={{ whiteSpace: "pre-line" }}
          >
            {fareApiError}
          </div>
        ) : null}
      </div>
      <div className="transport_colMain">
        <VehicleSelector selected={owVehicle} onSelect={setOwVehicle} />
        <div className="form_col">
          <div className="form_title">
            <h3>Hours </h3>
          </div>
          <div className="input_col input_col2">
            <div className="form-group">
              <div className="input_image">
                <img src="/assets/img/all-images/Clock-icon.png" alt="" />
              </div>
              <div className="input_box">
                <input
                  type="text"
                  className={`form-control${formErrors.hours ? " is-invalid" : ""}`}
                  placeholder="Hours (unlimited kms)"
                  value={hours ? `${hours} Hours` : ""}
                  ref={hoursInputRef}
                  onClick={() => onOpenHoursPicker(setHours)}
                  data-bs-toggle="modal"
                  data-bs-target="#hoursSelectModal"
                  readOnly
                  style={{ cursor: "pointer" }}
                />
                {formErrors.hours ? (
                  <div className="invalid-feedback d-block">
                    {formErrors.hours}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="input_image">
            <img src="assets/img/all-images/Clock-icon.png" alt="" />
          </div>
        </div>
        <div className="form_col">
          <div className="form_title">
            <h3>Location </h3>
            <a
              href="#!"
              className="info_modal"
              onClick={(e) => {
                e.preventDefault();
                addStop();
              }}
            >
              + Add Stop
            </a>
          </div>
          <div className="input_col">
            <div className="form-group position-relative">
              <div className="input_image">
                <img src="/assets/img/all-images/location_on.png" alt="" />
              </div>
              <div className="input_box">
                <input
                  type="text"
                  className={`form-control${formErrors.pickup ? " is-invalid" : ""}`}
                  placeholder="Pick-up"
                  value={pickup.address}
                  ref={pickupInputRef}
                  onChange={(e) => {
                    if (formErrors.pickup) {
                      setFormErrors((prev) => ({ ...prev, pickup: "" }));
                    }
                    setPickup({
                      address: e.target.value,
                      lat: null,
                      lng: null,
                    });
                    setFareApiError("");
                    setPickupServiceAreaError("");
                    setActiveField("pickup");
                    fetchSuggestions(e.target.value);
                  }}
                />
                {pickup.address && (
                  <img
                    src="/assets/img/all-images/close-icon.png"
                    alt=""
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setPickup({ address: "", lat: null, lng: null });
                      setPickupServiceAreaError("");
                    }}
                  />
                )}
                {activeField === "pickup" && suggestions.length > 0 && (
                  <ul className="autocomplete-suggestions">
                    {suggestions.map((s) => (
                      <li
                        key={s.place_id}
                        className="autocomplete-suggestion-item"
                        onClick={() => handlePlaceSelect(s, "pickup")}
                      >
                        <PlaceSuggestionLabel suggestion={s} />
                      </li>
                    ))}
                  </ul>
                )}
                {pickupServiceAreaError ? (
                  <div className="text-danger small mt-1">
                    {pickupServiceAreaError}
                  </div>
                ) : null}
                {formErrors.pickup ? (
                  <div className="invalid-feedback d-block">
                    {formErrors.pickup}
                  </div>
                ) : null}
              </div>
            </div>

            {stops.map((stop, index) => (
              <div className="form-group position-relative" key={index}>
                <div className="input_image">
                  <img src="/assets/img/all-images/location_on.png" alt="" />
                </div>
                <div className="input_box">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Add Stop"
                    value={stop.address}
                    onChange={(e) => {
                      const newStops = [...stops];
                      newStops[index] = {
                        ...stop,
                        address: e.target.value,
                        lat: null,
                        lng: null,
                      };
                      setStops(newStops);
                      setActiveField(`stop-${index}`);
                      fetchSuggestions(e.target.value);
                    }}
                  />
                  <img
                    src="/assets/img/all-images/close-icon.png"
                    alt=""
                    style={{ cursor: "pointer" }}
                    onClick={() => removeStop(index)}
                  />
                  {activeField === `stop-${index}` &&
                    suggestions.length > 0 && (
                      <ul className="autocomplete-suggestions">
                        {suggestions.map((s) => (
                          <li
                            key={s.place_id}
                            className="autocomplete-suggestion-item"
                            onClick={() =>
                              handlePlaceSelect(s, `stop-${index}`)
                            }
                          >
                            <PlaceSuggestionLabel suggestion={s} />
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              </div>
            ))}

            <div className="form-group position-relative">
              <div className="input_image">
                <img src="/assets/img/all-images/location_on.png" alt="" />
              </div>
              <div className="input_box">
                <input
                  type="text"
                  className={`form-control${formErrors.dropoff ? " is-invalid" : ""}`}
                  placeholder="Drop-off"
                  value={dropoff.address}
                  ref={dropoffInputRef}
                  onChange={(e) => {
                    if (formErrors.dropoff) {
                      setFormErrors((prev) => ({ ...prev, dropoff: "" }));
                    }
                    setDropoff({
                      address: e.target.value,
                      lat: null,
                      lng: null,
                    });
                    setFareApiError("");
                    setActiveField("dropoff");
                    fetchSuggestions(e.target.value);
                  }}
                />
                {dropoff.address && (
                  <img
                    src="/assets/img/all-images/close-icon.png"
                    alt=""
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setDropoff({ address: "", lat: null, lng: null })
                    }
                  />
                )}
                {activeField === "dropoff" && suggestions.length > 0 && (
                  <ul className="autocomplete-suggestions">
                    {suggestions.map((s) => (
                      <li
                        key={s.place_id}
                        className="autocomplete-suggestion-item"
                        onClick={() => handlePlaceSelect(s, "dropoff")}
                      >
                        <PlaceSuggestionLabel suggestion={s} />
                      </li>
                    ))}
                  </ul>
                )}
                {formErrors.dropoff ? (
                  <div className="invalid-feedback d-block">
                    {formErrors.dropoff}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="form_col">
          <div className="form_title">
            <h3>Schedule </h3>
          </div>
          <div className="input_col input_col2">
            <div className="form-group">
              <div className="input_image">
                <img src="/assets/img/all-images/Calendar-icon.png" alt="" />
              </div>
              <div className="input_box">
                <input
                  type="text"
                  className={`form-control${formErrors.schedule ? " is-invalid" : ""}`}
                  placeholder="Pick-up time"
                  value={schedule}
                  ref={scheduleInputRef}
                  onChange={(e) => {
                    if (formErrors.schedule) {
                      setFormErrors((prev) => ({ ...prev, schedule: "" }));
                    }
                    setSchedule(e.target.value);
                  }}
                  onClick={() => onOpenDatePicker(setSchedule, {}, schedule)}
                  data-bs-toggle="modal"
                  data-bs-target="#dateSelectModal"
                  readOnly
                  style={{ cursor: "pointer" }}
                />
                {formErrors.schedule ? (
                  <div className="invalid-feedback d-block">
                    {formErrors.schedule}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="form_col">
          <div className="form_title">
            <h3>Guest Info</h3>
          </div>
          <div className="input_col input_col2">
            <div className="form-group form-groupQuantity">
              <QuantityControl
                label="Passengers"
                value={cityPassengers}
                onChange={setCityPassengers}
                min={1}
                max={cityLimits.maxPassengers}
              />
              <QuantityControl
                label="Suitcase"
                value={citySuitcase}
                onChange={setCitySuitcase}
                min={0}
                max={cityLimits.maxSuitcase}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
